import { PredefinedMetric, VisualQueryFilter } from "src/types/visual";
import { CHART_COLORS } from "src/ui/colors";
import { NULL_OPTION_VALUE } from "./state/constants";
import { ChartTab, MeasurementScope } from "./types";
import { GraphType } from "@hightouch/lib/query/visual/types";

export const MAX_SELECTED_AUDIENCES_ALLOWED = 4;
export const MAX_LINES = 30;

export const DEFAULT_FILTER: VisualQueryFilter = {
  conditions: [],
};

export const ALL_DATA_MEASURING_SELECTION = {
  id: NULL_OPTION_VALUE, // Option value can't be null
  scope: MeasurementScope.AllData,
};

export enum MetricType {
  Goal = "Goal",
  Event = "Event",
}

export const PREDEFINED_METRIC_OPTIONS = [
  {
    id: PredefinedMetric.AudienceSize as string,
    name: "Audience size",
    eventModelId: null,
    description: null,
  },
];

export const AnalyticsColors = {
  Primary: {
    unfocused: "teal.200",
    secondary: "teal.300",
    primary: "teal.400",
    hover: "teal.500",
  },
  Secondary: {
    unfocused: "yellow.200",
    secondary: "yellow.300",
    primary: "yellow.400",
    hover: "yellow.500",
  },
  Tertiary: {
    unfocused: "green.200",
    secondary: "green.300",
    primary: "green.400",
    hover: "green.500",
  },
  Fourth: {
    unfocused: "indigo.200",
    secondary: "indigo.300",
    primary: "indigo.400",
    hover: "indigo.500",
  },
  Fifth: {
    unfocused: "pink.200",
    secondary: "pink.300",
    primary: "pink.400",
    hover: "pink.500",
  },
  Sixth: {
    unfocused: "gray.200",
    secondary: "gray.300",
    primary: "gray.400",
    hover: "gray.500",
  },
} as const;

/**
 * chartColors circle through the chart colors from dark to light.
 * @source: https://www.figma.com/design/jjcaU5bRbTtVDiZPpFzGE7/Charts-L-F-updates?node-id=4658-39681&m=dev
 *
 * Recharts needs to use css variables for colors instead of raw hex codes to stay aligned with the design system.
 */
export const chartColors = [
  {
    color: CHART_COLORS.blue.base,
  },
  {
    color: CHART_COLORS.yellow.base,
  },
  {
    color: CHART_COLORS.purple.base,
  },
  {
    color: CHART_COLORS.green.base,
  },
  {
    color: CHART_COLORS.orange.base,
  },
  {
    color: CHART_COLORS.brown.base,
  },
  {
    color: CHART_COLORS.pink.base,
  },
  {
    color: CHART_COLORS.blue.light,
  },
  {
    color: CHART_COLORS.yellow.light,
  },
  {
    color: CHART_COLORS.purple.light,
  },
  {
    color: CHART_COLORS.green.light,
  },
  {
    color: CHART_COLORS.orange.light,
  },
  {
    color: CHART_COLORS.brown.light,
  },
  {
    color: CHART_COLORS.pink.light,
  },
] as const;

/**
 * chartBaseColors circle through the chart colors in grouped pairs with both base and light colors.
 * Use this when charts use more than one color ex. gradients or areas.
 *
 * Recharts needs to use css variables for colors instead of raw hex codes to stay aligned with the design system.
 */
export const chartBaseColors = [
  {
    color: CHART_COLORS.blue.base,
    bg: CHART_COLORS.blue.light,
  },
  {
    color: CHART_COLORS.yellow.base,
    bg: CHART_COLORS.yellow.light,
  },
  {
    color: CHART_COLORS.purple.base,
    bg: CHART_COLORS.purple.light,
  },
  {
    color: CHART_COLORS.green.base,
    bg: CHART_COLORS.green.light,
  },
  {
    color: CHART_COLORS.orange.base,
    bg: CHART_COLORS.orange.light,
  },
  {
    color: CHART_COLORS.brown.base,
    bg: CHART_COLORS.brown.light,
  },
  {
    color: CHART_COLORS.pink.base,
    bg: CHART_COLORS.pink.light,
  },
] as const;

export const ChartOptions = [
  ChartTab.Insights,
  ChartTab.Funnel,
  ChartTab.SplitsMeasurement,
];

export const DASHBOARD_UNSUPPORTED_CHART_TYPES: (GraphType | ChartTab)[] = [
  GraphType.Table,
  ChartTab.SplitsMeasurement,
];
